// src/shell/TenantContext.jsx
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { resolveTenantId } from '../config/tenant-routing.js';
import DataService from '../services/DataService.js';
import useAppStore from '../store/appStore.js';
import { auth } from '../firebase/config.js';

// Map tenantId → data-tenant-skin attribute value + lazy CSS import.
// Extend this map as new tenant skins are added.
const TENANT_SKIN_MAP = {
  ncl: {
    skin: 'ncl-cruise',
    loadCss: () => import('../branding/skins/ncl-cruise/tenant-skin.css'),
  },
};

const TenantContext = createContext(null);

export function TenantProvider({ children }) {
  const [tenantId]     = useState(() => resolveTenantId(window.location.hostname));
  const [tenantConfig, setTenantConfig] = useState(null);
  const [loading, setLoading]           = useState(true);

  // Guard: once we have a non-null config, don't overwrite on subsequent
  // auth-state events (prevents flicker if onAuthStateChanged fires repeatedly).
  const loadedRef = useRef(false);

  async function fetchConfig(tid) {
    // Sync appStore BEFORE the Firestore call so any DataService method that
    // reads tenantId from the store (rather than an explicit arg) uses the
    // correct tenant instead of falling back to DEMO_TENANT_ID.
    useAppStore.getState().setTenantId(tid);
    try {
      return await DataService.getTenantConfig(tid);
    } catch {
      return null;
    }
  }

  // ── Primary load ────────────────────────────────────────────────────────
  // Fires once on mount. TenantProvider lives outside AuthGate, so Firebase
  // Auth may not have restored its cached session yet when this runs. If the
  // Firestore read is unauthenticated, permission-denied is caught silently
  // and cfg = null. The auth-listener effect below retries when auth settles.
  useEffect(() => {
    let active = true;
    fetchConfig(tenantId).then(cfg => {
      if (!active) return;
      if (cfg !== null) loadedRef.current = true;
      setTenantConfig(cfg);
      setLoading(false);
    });
    return () => { active = false; };
  }, [tenantId]);

  // ── Auth-state retry ────────────────────────────────────────────────────
  // When Firebase reports a signed-in user, re-fetch if we don't yet have a
  // config. Handles the race: primary load ran before auth was restored from
  // the browser's cached session, so getDoc was unauthenticated and denied.
  // Once auth is available the retry succeeds with the user's tenantId claim.
  useEffect(() => {
    if (!auth) return; // Firebase not configured — dev-local bypass

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user)            return; // signed out — leave existing state
      if (loadedRef.current) return; // already loaded — don't overwrite

      const cfg = await fetchConfig(tenantId);
      if (cfg !== null) {
        loadedRef.current = true;
        setTenantConfig(cfg);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [tenantId]);

  // ── Tenant skin activation ───────────────────────────────────────────────
  // Sets data-tenant-skin on <html> and dynamically imports the skin CSS so
  // the Vite bundle only loads tenant-specific overrides for the active tenant.
  useEffect(() => {
    const entry = TENANT_SKIN_MAP[tenantId];
    if (!entry) return;

    document.documentElement.setAttribute('data-tenant-skin', entry.skin);
    entry.loadCss().catch(() => {
      // Non-fatal: skin CSS failed to load; base theme still applies.
    });

    return () => {
      // Clean up when tenantId changes (e.g. hot-reload in dev).
      document.documentElement.removeAttribute('data-tenant-skin');
    };
  }, [tenantId]);

  return (
    <TenantContext.Provider value={{ tenantId, tenantConfig, loading }}>
      {children}
    </TenantContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used within TenantProvider');
  return ctx;
}
